import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
} from '@razorpay/blade/components';
import { useFormWithProvider } from 'forms';
import { ADD_FIELD_WIZARD_STEPS, EMPTY_FIELD_PREVIEW } from '../../constants';
import { useEmployeeFieldAccessRoles } from '../../hooks';
import {
  AddEmployeeFieldModalProps,
  EmployeeFieldAccess,
  EmployeeFieldFormData,
  EmployeeFieldValueType,
  FieldPreviewState,
} from '../../types';
import {
  buildEmployeeFieldInput,
  employeeFieldDetailsValidationSchema,
  getEmployeeFieldFormData,
  hasUnsavedEmployeeFieldChanges,
  parseEnumValuesInput,
} from '../../utils';
import { FieldDetailsStep } from '../AddFieldSteps/FieldDetailsStep';
import { PermissionsStep } from '../AddFieldSteps/PermissionsStep';
import { ReviewStep } from '../AddFieldSteps/ReviewStep';
import { StepRail } from '../AddFieldSteps/StepRail';

const LAST_STEP = ADD_FIELD_WIZARD_STEPS.length - 1;

export const AddEmployeeFieldModalContent = ({
  isOpen,
  onDismiss,
  onCreate,
  mode = 'create',
  initialField,
  onUpdate,
  isSubmitting = false,
}: AddEmployeeFieldModalProps) => {
  const rolesQuery = useEmployeeFieldAccessRoles();
  const { formData, setFormData, setFieldValue, validate } =
    useFormWithProvider<EmployeeFieldFormData>();

  // Wizard/UI-only state; the field values themselves live in the form provider.
  const [step, setStep] = useState(0);
  const [isExitConfirmOpen, setIsExitConfirmOpen] = useState(false);
  const [isTypeMenuOpen, setIsTypeMenuOpen] = useState(false);
  const [enumValueInput, setEnumValueInput] = useState('');
  const [openAccessRoleId, setOpenAccessRoleId] = useState<number | null>(null);
  const [showNoMemberRoles, setShowNoMemberRoles] = useState(false);
  const [isReviewAccessOpen, setIsReviewAccessOpen] = useState(false);
  const [preview, setPreview] = useState<FieldPreviewState>(EMPTY_FIELD_PREVIEW);

  const roles = rolesQuery.data.roles;
  const isEditMode = mode === 'edit' && Boolean(initialField);

  const resolvedAccessPolicy = useMemo(() => {
    const accessPolicy = formData.accessPolicy ?? {};
    return roles.reduce<Record<number, EmployeeFieldAccess>>((acc, role) => {
      acc[role.roleId] = role.isAccessLocked
        ? role.lockedAccess ?? role.defaultAccess
        : accessPolicy[role.roleId] ?? role.defaultAccess;
      return acc;
    }, {});
  }, [formData.accessPolicy, roles]);

  const canContinueFromDetails = validate(employeeFieldDetailsValidationSchema, {
    sideEffects: false,
  });
  const canContinueFromPermissions =
    !rolesQuery.isLoading && !rolesQuery.isError && roles.length > 0;
  const hasUnsavedChanges = hasUnsavedEmployeeFieldChanges(
    formData,
    isEditMode ? initialField : null,
  );

  const resetWizardUiState = () => {
    setStep(0);
    setIsTypeMenuOpen(false);
    setEnumValueInput('');
    setOpenAccessRoleId(null);
    setShowNoMemberRoles(false);
    setIsReviewAccessOpen(false);
    setPreview(EMPTY_FIELD_PREVIEW);
  };

  useEffect(() => {
    if (!isOpen) return;

    resetWizardUiState();
    setFormData(getEmployeeFieldFormData(isEditMode ? initialField : null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialField, isEditMode, isOpen]);

  const resetAndClose = () => {
    resetWizardUiState();
    setFormData(getEmployeeFieldFormData(null));
    setIsExitConfirmOpen(false);
    onDismiss();
  };

  const requestClose = () => {
    if (hasUnsavedChanges) {
      setIsExitConfirmOpen(true);
      return;
    }

    resetAndClose();
  };

  const handleTypeSelect = (nextValueType: EmployeeFieldValueType) => {
    setFieldValue('valueType', nextValueType);
    setIsTypeMenuOpen(false);
    setPreview(EMPTY_FIELD_PREVIEW);
    if (nextValueType !== 'ENUM') {
      setFieldValue('enumValues', []);
      setEnumValueInput('');
    }
  };

  const addEnumValues = () => {
    const committedValues = parseEnumValuesInput(enumValueInput);
    if (committedValues.length === 0) return;

    const currentValues = formData.enumValues ?? [];
    const nextValues = [
      ...currentValues,
      ...committedValues.filter((committedValue) => !currentValues.includes(committedValue)),
    ];
    setFieldValue('enumValues', nextValues);
    setEnumValueInput('');
  };

  const removeEnumValue = (valueToRemove: string) => {
    setFieldValue(
      'enumValues',
      (formData.enumValues ?? []).filter((currentValue) => currentValue !== valueToRemove),
    );
  };

  const handleAccessMenuToggle = (roleId: number) => {
    setOpenAccessRoleId((currentRoleId) => (currentRoleId === roleId ? null : roleId));
  };

  const handleAccessSelect = (roleId: number, access: EmployeeFieldAccess) => {
    setFieldValue('accessPolicy', { ...(formData.accessPolicy ?? {}), [roleId]: access });
    setOpenAccessRoleId(null);
  };

  const handlePreviewChange = (nextPreview: Partial<FieldPreviewState>) => {
    setPreview((currentPreview) => ({ ...currentPreview, ...nextPreview }));
  };

  const handleSubmit = () => {
    const input = buildEmployeeFieldInput(formData, resolvedAccessPolicy, initialField);

    if (isEditMode && initialField && onUpdate) {
      onUpdate(initialField.id, input);
      return;
    }

    onCreate(input);
  };

  const isContinueDisabled =
    (step === 0 && !canContinueFromDetails) || (step === 1 && !canContinueFromPermissions);

  return (
    <>
      <Modal isOpen={isOpen} onDismiss={requestClose} size="medium">
        <ModalHeader title={isEditMode ? 'Edit field' : 'Add a new field'} />
        <ModalBody padding="spacing.0">
          <Box
            display="grid"
            gridTemplateColumns="200px minmax(0, 1fr)"
            minHeight="600px"
            borderTopWidth="thin"
            borderTopColor="surface.border.gray.subtle">
            <StepRail currentStep={step} />
            {step === 0 ? (
              <FieldDetailsStep
                isTypeMenuOpen={isTypeMenuOpen}
                onTypeMenuToggle={() => setIsTypeMenuOpen((isMenuOpen) => !isMenuOpen)}
                onTypeSelect={handleTypeSelect}
                enumValueInput={enumValueInput}
                onEnumValueInputChange={setEnumValueInput}
                onEnumValueCommit={addEnumValues}
                onEnumValueRemove={removeEnumValue}
              />
            ) : null}
            {step === 1 ? (
              <PermissionsStep
                isRolesLoading={rolesQuery.isLoading}
                isRolesError={rolesQuery.isError}
                onRetryRoles={() => rolesQuery.refetch()}
                rolesWithMembers={roles.filter((role) => role.hasMembers)}
                rolesWithoutMembers={roles.filter((role) => !role.hasMembers)}
                resolvedAccessPolicy={resolvedAccessPolicy}
                openAccessRoleId={openAccessRoleId}
                onAccessMenuToggle={handleAccessMenuToggle}
                onAccessSelect={handleAccessSelect}
                showNoMemberRoles={showNoMemberRoles}
                onToggleNoMemberRoles={() => setShowNoMemberRoles((isShown) => !isShown)}
              />
            ) : null}
            {step === LAST_STEP ? (
              <ReviewStep
                roles={roles}
                resolvedAccessPolicy={resolvedAccessPolicy}
                isEditMode={isEditMode}
                isReviewAccessOpen={isReviewAccessOpen}
                onToggleReviewAccess={() => setIsReviewAccessOpen((isAccessOpen) => !isAccessOpen)}
                preview={preview}
                onPreviewChange={handlePreviewChange}
              />
            ) : null}
          </Box>
        </ModalBody>
        <ModalFooter>
          <Box
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="space-between">
            <Button variant="secondary" onClick={requestClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
            <Box display="flex" alignItems="center" gap="spacing.3">
              {step > 0 ? (
                <Button
                  variant="secondary"
                  isDisabled={isSubmitting}
                  onClick={() => setStep((currentStep) => currentStep - 1)}>
                  Back
                </Button>
              ) : null}
              {step < LAST_STEP ? (
                <Button
                  isDisabled={isContinueDisabled}
                  onClick={() => setStep((currentStep) => currentStep + 1)}>
                  Continue
                </Button>
              ) : (
                <Button isLoading={isSubmitting} onClick={handleSubmit}>
                  {isEditMode ? 'Save changes' : 'Add field'}
                </Button>
              )}
            </Box>
          </Box>
        </ModalFooter>
      </Modal>

      <Modal isOpen={isExitConfirmOpen} onDismiss={() => setIsExitConfirmOpen(false)} size="small">
        <ModalHeader title="Exit without saving?" />
        <ModalBody>
          <Text>Your changes will be lost if you exit now.</Text>
        </ModalBody>
        <ModalFooter>
          <Box display="flex" gap="spacing.3" justifyContent="flex-end" width="100%">
            <Button variant="secondary" onClick={() => setIsExitConfirmOpen(false)}>
              Keep editing
            </Button>
            <Button color="negative" onClick={resetAndClose}>
              Exit
            </Button>
          </Box>
        </ModalFooter>
      </Modal>
    </>
  );
};
